// ============================================================================
// ITEM UTILITY CONTEXT - Constants, Enums, and Utility Functions
// ============================================================================
// This context provides constants, enums, and utility functions for item-related
// operations (projects and collections). It contains no state and only provides static utilities.
//
// Note: "Item" refers to projects and collections, NOT map entities (pins, polygons, etc.)

import React, { createContext, useContext } from 'react';
import { hasPublicAccess, parseAccessMapFromBackend } from '../../../utils/accessMapUtils';
import { PUBLIC_ACCESS_OPTIONS } from '../../../utils/accessMapConstants';
import { getPublicAccessOptionFromAccessMap } from '../../../utils/accessMapRules';
import {
  PROJECT_STATUSES,
  COLLECTION_STATUSES,
  VISIBILITY_TYPES,
  formatStatusName,
  formatVisibilityName,
  formatPublicAccessLevelName,
  getNonDeletedStatuses
} from '../../constants/itemConstants';

// ============================================================================
// CONTEXT CREATION AND EXPORTS
// ============================================================================

const ItemUtilityContext = createContext();

export const useItemUtility = () => useContext(ItemUtilityContext);

// For backward compatibility, also export as useProjectUtility
export const useProjectUtility = () => useContext(ItemUtilityContext);

// ============================================================================
// RE-EXPORT CONSTANTS FROM ITEM CONSTANTS
// ============================================================================
// These are re-exported for backward compatibility and convenience

export { PROJECT_STATUSES, COLLECTION_STATUSES, VISIBILITY_TYPES };

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generate consistent color classes for tags
 * This ensures the same tag always gets the same color across the app
 *
 * @param {string} tag - The tag text
 * @returns {string} - CSS class name for the tag color
 */
const getTagColorClass = (tag) => {
  // Return default gray for empty/null tags
  if (!tag) return "tag-gray";

  // Simple hash function to generate consistent colors for tags
  // This creates a deterministic color based on the tag text
  const hash = Array.from(tag.toLowerCase()).reduce((acc, char) =>
    ((acc << 5) - acc) + char.charCodeAt(0), 0);

  // Available color classes for tags
  const colorClasses = ["tag-blue", "tag-green", "tag-purple", "tag-orange", "tag-red", "tag-teal"];

  // Use modulo to get a consistent index for this tag
  const colorIndex = Math.abs(hash) % colorClasses.length;

  return colorClasses[colorIndex];
};

/**
 * Get CSS color class for item status (project or collection)
 * This provides consistent styling for different statuses
 *
 * @param {string} status - The status constant
 * @returns {string} - CSS class name for the status color
 */
const getStatusColorClass = (status) => {
  // Map each status to its corresponding CSS class
  const statusColorMap = {
    DRAFT: "status-draft",
    LIVE: "status-live",
    ARCHIVED: "status-archived",
    PENDING_APPROVAL: "status-pending",
    APPROVED: "status-approved",
    REJECTED: "status-rejected",
    DELETED: "status-deleted"
  };

  // Return the mapped class or default to draft status
  return statusColorMap[status] || "status-draft";
};

/**
 * Determine project visibility (PUBLIC or RESTRICTED)
 * This checks if a project has any public access levels
 *
 * @param {Object} project - The project object with accessMap
 * @returns {string} - "PUBLIC" or "RESTRICTED"
 */
const getProjectVisibility = (project) => {
  // Check if any role has public access using utility function
  const isPublic = hasPublicAccess(project?.accessMap);

  // Return appropriate visibility type
  return isPublic ? VISIBILITY_TYPES.PUBLIC : VISIBILITY_TYPES.RESTRICTED;
};

/**
 * Determine collection visibility (PUBLIC or RESTRICTED)
 * This checks if a collection has any public access levels
 * Alias for getProjectVisibility since they work identically
 *
 * @param {Object} collection - The collection object with accessMap
 * @returns {string} - "PUBLIC" or "RESTRICTED"
 */
const getCollectionVisibility = (collection) => {
  const isPublic = hasPublicAccess(collection?.accessMap);
  return isPublic ? VISIBILITY_TYPES.PUBLIC : VISIBILITY_TYPES.RESTRICTED;
};

/**
 * Generic function to determine item visibility
 * Works for both projects and collections
 *
 * @param {Object} item - The item object (project or collection) with accessMap
 * @returns {string} - "PUBLIC" or "RESTRICTED"
 */
const getItemVisibility = (item) => {
  const isPublic = hasPublicAccess(item?.accessMap);
  return isPublic ? VISIBILITY_TYPES.PUBLIC : VISIBILITY_TYPES.RESTRICTED;
};

/**
 * Get public access level for an item (project or collection) for card display.
 * Uses publicAccessPolicy (DEFAULT|NONE|VIEWER|CONTRIBUTOR); empty → default.
 *
 * @param {Object} item - The item with accessMap from API
 * @returns {string} - 'default' | 'protected' | 'view' | 'edit'
 */
const getItemPublicAccessLevel = (item) => {
  if (!item?.accessMap) return PUBLIC_ACCESS_OPTIONS.PROTECTED;
  const parsed = parseAccessMapFromBackend(item.accessMap);
  return getPublicAccessOptionFromAccessMap(parsed);
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function ItemUtilityProvider({ children }) {
  // Create the context value with all utility functions and constants
  const contextValue = {
    // Constants
    PROJECT_STATUSES,
    COLLECTION_STATUSES,
    VISIBILITY_TYPES,

    // Utility functions (imported from itemConstants)
    formatStatusName,
    formatVisibilityName,
    formatPublicAccessLevelName,
    getNonDeletedStatuses,

    // Utility functions (defined here)
    getTagColorClass,
    getStatusColorClass,
    getProjectVisibility,
    getCollectionVisibility,
    getItemVisibility,
    getItemPublicAccessLevel,
  };

  return (
    <ItemUtilityContext.Provider value={contextValue}>
      {children}
    </ItemUtilityContext.Provider>
  );
}

// For backward compatibility, also export as ProjectUtilityProvider
export const ProjectUtilityProvider = ItemUtilityProvider;
